import Stealthex, { StealthexToken, Range } from '../clients/Stealthex'
import { logger } from '../logger'
import { useCallback, useEffect, useMemo, useState } from 'react'

const PRICE_FETCH_INTERVAL = 10000

interface Props {
  from?: StealthexToken
  to?: StealthexToken
}

const useStealthex = ({ from, to }: Props) => {
  const [tokens, setTokens] = useState<StealthexToken[]>([])
  const [pairs, setPairs] = useState<string[]>([])
  const [price, setPrice] = useState<number>()
  const [priceLoading, setPriceLoading] = useState(true)
  const [range, setRange] = useState<Range>()

  const client = useMemo(() => {
    return new Stealthex()
  }, [])

  useEffect(() => {
    client
      .getAvailableTokens()
      .then(setTokens)
      .catch((e) => logger.error('Failed to get Stealthex tokens', { originalError: e }))
  }, [client])

  useEffect(() => {
    if (!from) return

    client
      .getPairs(from.id)
      .then(setPairs)
      .catch((e) => logger.error('Failed to get Stealthex pairs', { originalError: e }))
  }, [client, from])

  const destinationTokens = useMemo(() => {
    return tokens.filter((token) => pairs.includes(token.id))
  }, [tokens, pairs])

  const fetchEstimatedAmount = useCallback(
    async (amount: number) => {
      if (!from || !to) return

      return client.getEstimatedAmount(from.id, to.id, amount)
    },
    [client, from, to]
  )

  const createExchange = useCallback(
    (amountFrom: number, addressTo: string) => {
      if (!from || !to) return Promise.reject('From & to currencies have to be selected')

      return client.createExchange({
        currencyFrom: from.id,
        currencyTo: to.id,
        addressTo,
        amountFrom,
      })
    },
    [from, to, client]
  )

  useEffect(() => {
    const interval = setInterval(() => {
      setPriceLoading(true)

      fetchEstimatedAmount(1)
        .then(setPrice)
        .catch((e) => logger.error('Failed to get Stealhex price', { originalError: e }))
        .finally(() => setPriceLoading(false))
    }, PRICE_FETCH_INTERVAL)

    return () => {
      clearInterval(interval)
    }
  }, [setPriceLoading, fetchEstimatedAmount])

  useEffect(() => {
    if (!from || !to) return

    client
      .getRange(from.id, to.id)
      .then(setRange)
      .catch((e) => logger.error('Failed to get Stealthex range', { originalError: e }))
  }, [from, to, client])

  const value = useMemo(() => {
    return {
      tokens,
      destinationTokens,
      price,
      priceLoading,
      fetchEstimatedAmount,
      range,
      createExchange,
    }
  }, [tokens, destinationTokens, price, priceLoading, fetchEstimatedAmount, range, createExchange])

  return value
}

export default useStealthex
